// src/dashboard/store/phone-call-drawer-store.ts
import { create } from 'zustand';

/**
 * Cross-shell UI state: is the phone-call drawer open right now?
 *
 * Why this exists (Codex follow-up on PR #225, shell-level containment):
 * `dashboard-view.tsx` already marks its own header + worklist `inert` while
 * the drawer is open (Codex P1 fix), but that only reaches DOM the routed
 * page itself renders. The persistent `PractitionerNav` app rail is mounted
 * one level up, in `AppShell` (`src/app/app-shell.tsx`) — a sibling of the
 * routed `<Outlet/>`, not a descendant of `DashboardView`. A React Router
 * `<Outlet/>` gives a page no way to reach up into its shell, so the rail
 * stayed clickable: a user could Tab/click to another nav item while the
 * drawer was open and lose the phone-call draft.
 *
 * This tiny store is the "lightweight mechanism" that closes that gap —
 * mirrors the existing `usePrintPreviewStore` pattern
 * (`src/billing/store/print-preview-store.ts`) for pure, transient UI state
 * shared across a shell boundary. `DashboardPage` sets `open` when the
 * drawer opens/closes; `AppShell` reads it to mark the nav rail `inert` too.
 * Zustand owns this per the project state rule — it is UI state, never
 * server state.
 */
interface PhoneCallDrawerState {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const usePhoneCallDrawerStore = create<PhoneCallDrawerState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));
